import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import axios from "axios";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";
import { StatPanel, StatTile, formatUTCDateTime, money } from "../shared/AffiliateOverview";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    alignItems: "stretch",
    justifyContent: "flex-start",
    padding: "2em",
  },
  mainHeading: {
    textAlign: "center",
    marginBottom: "1em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  card: {
    width: "100%",
    maxWidth: 1200,
    marginTop: "1em",
    padding: "0 2em 2em 2em",
    alignSelf: "center",
  },
  form: {
    padding: "0 1em 1em 1em",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1em",
  },
  input: {
    marginTop: "1em",
    width: "100%",
    maxWidth: 420,
  },
  filters: {
    display: "flex",
    gap: "0.5em",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  actions: {
    padding: "0 1em 1em 1em",
    display: "flex",
    gap: "1em",
    justifyContent: "center",
  },
  tableContainer: {
    marginTop: "1.5em",
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    overflowX: "auto",
    padding: "0 1.5em 1.5em 1.5em",
  },
  table: {
    width: "100%",
  },
  tableCell: {
    padding: "8px 16px",
    border: "1px solid #e0e0e0",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
  },
  sectionTitle: {
    marginTop: "2em",
    marginBottom: "1em",
    color: theme.palette.primary.main,
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.grey[500],
    fontStyle: "italic",
    margin: "2em 0",
  },
  helperText: {
    marginTop: "0.75em",
    color: theme.palette.grey[600],
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    padding: "2em",
  },
  tabBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    margin: "0 -2em 1.5em -2em",
  },
  tab: {
    fontSize: "1.05rem",
    fontWeight: 600,
    textTransform: "none",
    minHeight: 64,
    letterSpacing: 0.2,
  },
  tabIndicator: {
    height: 3,
  },
  rangeBar: {
    display: "flex",
    gap: "0.75em",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: "1em",
  },
  rangeInput: {
    width: 190,
  },
}));

const PROVIDERS = ["all", "vdx", "partnerize", "awin"] as const;
type Provider = (typeof PROVIDERS)[number];

const PROVIDER_LABEL: Record<string, string> = {
  all: "All networks",
  vdx: "VDX",
  partnerize: "Partnerize",
  awin: "Awin",
};

const MONEY_KEYS = ["order_value", "rewards_amount", "gross_amount"];

const OVERVIEW_DAY_OPTIONS = [7, 30, 90] as const;

interface AffiliateUserDetails {
  user_id: number;
  email: string;
  providers: string[];
  sprive_plus_status: string;
  sprive_plus_enabled: boolean;
  dashboard_landed_at: string | null;
}

interface AffiliateSummary {
  provider: string;
  transactions: number;
  order_value: number;
  rewards_amount: number;
  pending_rewards: number;
  paid_rewards: number;
}

interface AffiliateNetworkStats {
  provider: string;
  transactions: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  approval_rate_pct: number;
  sale_amount: number;
  approved_sale_amount: number;
  commission: number;
  reward_total: number;
  reward_pending: number;
  reward_earned: number;
  reward_paid: number;
  reward_cancelled: number;
  user_amount: number;
  paid_to_users: number;
  distinct_users: number;
  distinct_brands: number;
}

interface AffiliateTransaction {
  [key: string]: any;
}

const isReleasable = (row: AffiliateTransaction) => {
  if (!row.reward_id) return false;
  if (row.provider === "vdx") return row.reward_status === "approved" && !Number(row.is_inflight);
  return row.reward_status === "pending";
};

const releaseTooltip = (row: AffiliateTransaction) => {
  if (!row.reward_id) return "No reward row for this transaction yet";
  if (isReleasable(row)) return "Change when this reward is released";
  if (row.provider === "vdx" && Number(row.is_inflight)) {
    return "Already in flight - the date can no longer be changed";
  }
  return `Already ${row.reward_status} - the date can only be changed while pending`;
};

const AffiliateUserActivity = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [provider, setProvider] = useState<Provider>("all");
  const [userDetails, setUserDetails] = useState<AffiliateUserDetails | null>(null);
  const [summary, setSummary] = useState<AffiliateSummary[]>([]);
  const [overview, setOverview] = useState<AffiliateNetworkStats[]>([]);
  const [tab, setTab] = useState(0);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewDays, setOverviewDays] = useState<number>(OVERVIEW_DAY_OPTIONS[0]);
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [transactions, setTransactions] = useState<AffiliateTransaction[]>([]);
  const [headers, setHeaders] = useState<{ [key: string]: string }>({});
  const [hasDetails, setHasDetails] = useState(false);

  const [editRow, setEditRow] = useState<AffiliateTransaction | null>(null);
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const classes = useStyles();
  const notify = useNotify();

  const toInputValue = (value: string | null) => {
    const date = value ? new Date(value) : new Date();
    if (isNaN(date.getTime())) return "";
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  };

  const getHeaderLabel = (key: string, defaultLabel: string) => {
    if (key === "purchase_status") return `${defaultLabel} (Network)`;
    if (key === "reward_status") return `${defaultLabel} (Sprive)`;
    return defaultLabel;
  };

  const load = useCallback(
    async (searchEmail: string, nextProvider: Provider) => {
      setLoading(true);
      try {
        const response = await axios.get(
          ENDPOINTS.USER_AFFILIATE_ACTIVITY,
          getConfigGet({ email: searchEmail, provider: nextProvider })
        );
        const firstData = (response?.data as any)?.data?.[0];
        if (!firstData?.userDetails?.length) {
          setHasDetails(false);
          notify("No affiliate activity found for this email", "warning");
          return;
        }

        setUserDetails(firstData.userDetails[0]);
        setSummary(firstData.summary ?? []);
        setOverview(firstData.overview ?? []);
        setHasDetails(true);

        if (firstData.transactionDetails?.length) {
          const headerRow = firstData.transactionDetails[0];
          const headerMapping: { [key: string]: string } = {};
          Object.keys(headerRow).forEach((key) => {
            headerMapping[key.toLowerCase()] = headerRow[key];
          });
          setHeaders(headerMapping);
          setTransactions(firstData.transactionDetails.filter((_: any, i: number) => i > 0));
        } else {
          setHeaders({});
          setTransactions([]);
        }
        notify("Affiliate activity loaded", "info");
      } catch (error: any) {
        console.error("Error fetching affiliate activity:", error);
        notify(
          error?.response?.data?.message || "Failed to fetch affiliate activity",
          "error"
        );
        setHasDetails(false);
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  const handleEmailSubmit = () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }
    setUserDetails(null);
    setTransactions([]);
    setHeaders({});
    setHasDetails(false);
    load(email.trim(), provider);
  };

  const handleProviderChange = (next: Provider) => {
    setProvider(next);
    if (hasDetails && email.trim()) load(email.trim(), next);
  };

  const handleEnableSprivePlus = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setAddLoading(true);
    try {
      await axios.post(
        ENDPOINTS.USER_AFFILIATE_ACTIVITY,
        {},
        { headers: getConfigPost(), params: { email: email.trim() } }
      );
      notify("Sprive Plus enabled - this unlocks every affiliate network", "info");
      load(email.trim(), provider);
    } catch (error: any) {
      console.error("Error enabling Sprive Plus:", error);
      notify(error?.response?.data?.message || "Failed to enable Sprive Plus", "error");
    } finally {
      setAddLoading(false);
    }
  };

  const canRefresh = provider === "vdx";

  const handleRefresh = async () => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    setRefreshLoading(true);
    try {
      await axios.patch(
        ENDPOINTS.USER_AFFILIATE_ACTIVITY,
        { provider },
        { headers: getConfigPost(), params: { email: email.trim() } }
      );
      notify("Transactions refreshed from the network", "info");
      load(email.trim(), provider);
    } catch (error: any) {
      console.error("Error refreshing transactions:", error);
      notify(error?.response?.data?.message || "Failed to refresh transactions", "error");
    } finally {
      setRefreshLoading(false);
    }
  };

  const openEditDialog = (transaction: AffiliateTransaction) => {
    setEditRow(transaction);
    setEditDate(toInputValue(transaction.transaction_date));
  };

  const closeEditDialog = () => {
    setEditRow(null);
    setEditDate("");
  };

  const handleReleaseDateSave = async () => {
    if (!editRow || !editDate) {
      notify("Please pick a transaction date", "error");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        ENDPOINTS.USER_AFFILIATE_ACTIVITY,
        {
          provider: editRow.provider,
          reward_id: editRow.reward_id,
          transaction_date: `${editDate.replace("T", " ")}:00`,
        },
        { headers: getConfigPost(), params: { email: email.trim() } }
      );

      notify("Transaction date updated. The next transfer run will pay it.", "info");
      closeEditDialog();
      load(email.trim(), provider);
    } catch (error: any) {
      console.error("Error updating transaction date:", error);
      notify(error?.response?.data?.message || "Failed to update transaction date", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderSummary = () => {
    if (!hasDetails || !summary.length) return null;

    const combined = summary.find((s) => s.provider === "all");
    const perNetwork = summary.filter((s) => s.provider !== "all");

    const tiles: StatTile[] = perNetwork.map((s) => ({
      value: s.transactions,
      label: `${PROVIDER_LABEL[s.provider] ?? s.provider} transactions`,
    }));

    const valueTiles: StatTile[] = [
      { value: money(combined?.order_value ?? 0), label: "Order value" },
      { value: money(combined?.rewards_amount ?? 0), label: "Rewards total" },
      { value: money(combined?.pending_rewards ?? 0), label: "Pending rewards" },
      { value: money(combined?.paid_rewards ?? 0), label: "Paid to user" },
    ];

    return <StatPanel title="This User's Affiliate Activity" tiles={tiles} valueTiles={valueTiles} />;
  };

  const loadOverview = useCallback(async (
    nextProvider: Provider,
    days: number,
    from?: string,
    to?: string
  ) => {
    setOverviewLoading(true);
    try {
      const range = from && to ? { from, to } : { days };
      const response = await axios.get(
        ENDPOINTS.USER_AFFILIATE_ACTIVITY,
        getConfigGet({ provider: nextProvider, ...range })
      );
      setOverview((response?.data as any)?.data?.[0]?.overview ?? []);
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to load affiliate performance", "error");
    } finally {
      setOverviewLoading(false);
    }
  }, [notify]);

  const customRange = Boolean(rangeFrom && rangeTo);

  useEffect(() => {
    if (tab !== 1) return;
    if (rangeFrom && !rangeTo) return;
    if (rangeTo && !rangeFrom) return;
    loadOverview(provider, overviewDays, rangeFrom || undefined, rangeTo || undefined);
  }, [tab, provider, overviewDays, rangeFrom, rangeTo, loadOverview]);

  const renderPerformance = () => {
    if (overviewLoading && !overview.length) {
      return (
        <Paper className={classes.tableContainer}>
          <div className={classes.loader}><CircularProgress /></div>
        </Paper>
      );
    }
    if (!overview.length) return null;

    const combined = overview.find((o) => o.provider === "all");
    const perNetwork = overview.filter((o) => o.provider !== "all");

    return (
      <Paper className={classes.tableContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Affiliate Performance — {PROVIDER_LABEL[provider] ?? provider} —{" "}
          {customRange ? `${rangeFrom} to ${rangeTo}` : `last ${overviewDays} days`}
        </Typography>
        <div className={classes.rangeBar}>
          {OVERVIEW_DAY_OPTIONS.map((days) => (
            <Chip
              key={days}
              label={`Last ${days} days`}
              color={!customRange && overviewDays === days ? "primary" : "default"}
              onClick={() => {
                setRangeFrom("");
                setRangeTo("");
                setOverviewDays(days);
              }}
              disabled={overviewLoading}
            />
          ))}
          <TextField
            className={classes.rangeInput}
            label="From"
            type="date"
            value={rangeFrom}
            onChange={(e) => setRangeFrom(e.target.value)}
            variant="outlined"
            size="small"
            disabled={overviewLoading}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            className={classes.rangeInput}
            label="To"
            type="date"
            value={rangeTo}
            onChange={(e) => setRangeTo(e.target.value)}
            variant="outlined"
            size="small"
            disabled={overviewLoading}
            InputLabelProps={{ shrink: true }}
          />
          {customRange && (
            <Chip
              label="Clear range"
              onDelete={() => {
                setRangeFrom("");
                setRangeTo("");
              }}
              onClick={() => {
                setRangeFrom("");
                setRangeTo("");
              }}
              disabled={overviewLoading}
            />
          )}
        </div>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Network</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">Approved</TableCell>
              <TableCell align="right">Rejected</TableCell>
              <TableCell align="right">Approval&nbsp;%</TableCell>
              <TableCell align="right">Sales</TableCell>
              <TableCell align="right">Commission</TableCell>
              <TableCell align="right">Owed to users</TableCell>
              <TableCell align="right">Paid to users</TableCell>
              <TableCell align="right">Shoppers</TableCell>
              <TableCell align="right">Brands</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {perNetwork.map((o) => (
              <TableRow key={o.provider} hover>
                <TableCell>{PROVIDER_LABEL[o.provider] ?? o.provider}</TableCell>
                <TableCell align="right">{o.transactions}</TableCell>
                <TableCell align="right">{o.pending_count}</TableCell>
                <TableCell align="right">{o.approved_count}</TableCell>
                <TableCell align="right">{o.rejected_count}</TableCell>
                <TableCell align="right">{o.approval_rate_pct}%</TableCell>
                <TableCell align="right">{money(o.sale_amount)}</TableCell>
                <TableCell align="right">{money(o.commission)}</TableCell>
                <TableCell align="right">{money(o.user_amount)}</TableCell>
                <TableCell align="right">{money(o.paid_to_users)}</TableCell>
                <TableCell align="right">{o.distinct_users}</TableCell>
                <TableCell align="right">{o.distinct_brands}</TableCell>
              </TableRow>
            ))}
            {combined && (
              <TableRow>
                <TableCell><strong>{PROVIDER_LABEL.all}</strong></TableCell>
                <TableCell align="right"><strong>{combined.transactions}</strong></TableCell>
                <TableCell align="right"><strong>{combined.pending_count}</strong></TableCell>
                <TableCell align="right"><strong>{combined.approved_count}</strong></TableCell>
                <TableCell align="right"><strong>{combined.rejected_count}</strong></TableCell>
                <TableCell align="right"><strong>{combined.approval_rate_pct}%</strong></TableCell>
                <TableCell align="right"><strong>{money(combined.sale_amount)}</strong></TableCell>
                <TableCell align="right"><strong>{money(combined.commission)}</strong></TableCell>
                <TableCell align="right"><strong>{money(combined.user_amount)}</strong></TableCell>
                <TableCell align="right"><strong>{money(combined.paid_to_users)}</strong></TableCell>
                <Tooltip title="Customers who shopped on at least one network — not the sum, the same person can use several">
                  <TableCell align="right"><strong>{combined.distinct_users}</strong></TableCell>
                </Tooltip>
                <TableCell align="right"><strong>{combined.distinct_brands}</strong></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderUserDetails = () => {
    if (!hasDetails || !userDetails) return null;

    return (
      <Paper className={classes.tableContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          User Details
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>User ID</TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Sprive Plus Status
              </TableCell>
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>
                Dashboard Landed At
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell}>{userDetails.user_id}</TableCell>
              <TableCell className={classes.tableCell}>{userDetails.sprive_plus_status}</TableCell>
              <TableCell className={classes.tableCell}>
                {userDetails.dashboard_landed_at
                  ? formatUTCDateTime(userDetails.dashboard_landed_at)
                  : "-"}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    );
  };

  const renderTransactions = () => {
    if (!hasDetails) return null;

    if (!transactions.length) {
      return (
        <Paper className={classes.tableContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Affiliate Transactions
          </Typography>
          <Typography className={classes.noDataMessage}>No transactions found</Typography>
        </Paper>
      );
    }

    const keys = Object.keys(headers);

    return (
      <Paper className={classes.tableContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Affiliate Transactions
        </Typography>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {keys.map((key) => (
                <TableCell key={key} className={`${classes.tableCell} ${classes.tableHeader}`}>
                  {getHeaderLabel(key, headers[key])}
                </TableCell>
              ))}
              <TableCell className={`${classes.tableCell} ${classes.tableHeader}`}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction, index) => (
              <TableRow key={`${transaction.provider}-${transaction.external_id}-${index}`}>
                {keys.map((key) => (
                  <TableCell key={key} className={classes.tableCell}>
                    {key === "provider" ? (
                      <Chip size="small" label={PROVIDER_LABEL[transaction[key]] ?? transaction[key]} />
                    ) : MONEY_KEYS.includes(key) && transaction[key] !== null ? (
                      money(transaction[key])
                    ) : key.includes("date") && transaction[key] ? (
                      formatUTCDateTime(transaction[key])
                    ) : (
                      transaction[key] ?? "-"
                    )}
                  </TableCell>
                ))}
                <TableCell className={classes.tableCell}>
                  <Tooltip title={releaseTooltip(transaction)}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={!isReleasable(transaction)}
                        onClick={() => openEditDialog(transaction)}
                      >
                        Edit Transaction Date
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <div className={classes.main}>
      <Typography variant="h3" component="h1" className={classes.mainHeading}>
        Affiliate User Activity
      </Typography>

      <Paper className={classes.card}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          className={classes.tabBar}
          classes={{ indicator: classes.tabIndicator }}
        >
          <Tab label="User Activity" className={classes.tab} />
          <Tab label="Affiliate Performance" className={classes.tab} />
        </Tabs>

        {tab === 1 && (
          <div className={classes.filters}>
            {PROVIDERS.map((option) => (
              <Chip
                key={option}
                label={PROVIDER_LABEL[option]}
                color={provider === option ? "primary" : "default"}
                onClick={() => setProvider(option)}
                disabled={overviewLoading}
              />
            ))}
          </div>
        )}

        <div className={classes.form} style={{ display: tab === 0 ? undefined : "none" }}>
          <TextField
            className={classes.input}
            label="Enter Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            fullWidth
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleEmailSubmit();
            }}
          />

          <div className={classes.filters}>
            {PROVIDERS.map((option) => (
              <Chip
                key={option}
                label={PROVIDER_LABEL[option]}
                color={provider === option ? "primary" : "default"}
                onClick={() => handleProviderChange(option)}
                disabled={loading}
              />
            ))}
          </div>

          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEmailSubmit}
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Searching..." : "Search Affiliate Activity"}
            </Button>

            <Tooltip
              title={
                canRefresh
                  ? "Re-pulls this user's purchases from the VDX API"
                  : "Only VDX supports an on-demand pull - pick VDX to refresh"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRefresh}
                  disabled={refreshLoading || !canRefresh || !hasDetails}
                  startIcon={refreshLoading ? <CircularProgress size={20} /> : null}
                >
                  {refreshLoading ? "Refreshing..." : "Refresh Transactions"}
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                userDetails?.sprive_plus_enabled
                  ? "Sprive Plus is already enabled for this user"
                  : "Enables Sprive Plus, which unlocks every affiliate network"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleEnableSprivePlus}
                  disabled={addLoading || !email.trim() || !!userDetails?.sprive_plus_enabled}
                  startIcon={addLoading ? <CircularProgress size={20} /> : null}
                >
                  {addLoading ? "Adding..." : "Enable Sprive Plus"}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        {tab === 0 && !hasDetails && email && !loading && (
          <Typography className={classes.noDataMessage}>
            No affiliate activity found for this email
          </Typography>
        )}
      </Paper>

      {tab === 1 && renderPerformance()}
      {tab === 0 && renderSummary()}
      {tab === 0 && renderUserDetails()}
      {tab === 0 && renderTransactions()}

      <Dialog open={!!editRow} onClose={closeEditDialog}>
        <DialogTitle>Edit Transaction Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {PROVIDER_LABEL[editRow?.provider] ?? editRow?.provider} &mdash; {editRow?.brand_name}{" "}
            &mdash; {money(editRow?.rewards_amount)}
          </Typography>
          <TextField
            className={classes.input}
            label="Transaction Date (UTC)"
            type="datetime-local"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" className={classes.helperText} component="p">
            Setting a date in the past releases the reward on the next affiliate rewards transfer
            run. The money is not moved by saving this.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReleaseDateSave}
            color="primary"
            variant="contained"
            disabled={saving || !editDate}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default AffiliateUserActivity;
